import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const FeaturesSection = () => {
  const [language, setLanguage] = useState('en');
  const [activeFeature, setActiveFeature] = useState(0);
  const sectionRef = useRef(null);
  const featuresRef = useRef([]);
  const demoRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      // Animate feature cards
      featuresRef?.current?.forEach((feature, index) => {
        if (feature) {
          gsap?.fromTo(
            feature,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: feature,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });

      // Animate demo section
      gsap?.fromTo(
        demoRef?.current,
        { scale: 0.9, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: demoRef?.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'Powerful Features That Work For You',
      subtitle:
        'Explore the tools that transform your booking process from chaos to seamless automation',
      features: [
        {
          icon: 'Calendar',
          title: 'Smart Calendar Management',
          description:
            'AI-powered scheduling that prevents conflicts and optimizes your time slots automatically.',
          demo: {
            title: 'Calendar Demo',
            content:
              'Watch how our smart calendar automatically detects conflicts, suggests optimal time slots, and syncs across all your devices in real-time.',
          },
          benefits: [
            'Conflict prevention',
            'Auto-optimization',
            'Real-time sync',
            'Multi-staff support',
          ],
        },
        {
          icon: 'Bot',
          title: 'AI Auto-Booking Assistant',
          description:
            'Intelligent chatbot handles complex booking scenarios, understands context, and provides human-like interactions.',
          demo: {
            title: 'AI Assistant Demo',
            content:
              'Experience how our AI understands natural language, handles complex requests, and provides personalized responses 24/7.',
          },
          benefits: [
            '24/7 availability',
            'Natural language',
            'Context awareness',
            'Multi-language support',
          ],
        },
        {
          icon: 'MessageSquare',
          title: 'Multi-Channel Integration',
          description:
            'Seamlessly connect Telegram, WhatsApp, Instagram, and your website through one unified platform.',
          demo: {
            title: 'Integration Demo',
            content:
              'See how bookings from different channels automatically sync to your central dashboard with unified customer profiles.',
          },
          benefits: [
            'Unified dashboard',
            'Cross-platform sync',
            'Customer profiles',
            'Channel analytics',
          ],
        },
        {
          icon: 'Bell',
          title: 'Smart Reminder System',
          description:
            'Automated reminders via preferred channels reduce no-shows by 85% with personalized messaging.',
          demo: {
            title: 'Reminders Demo',
            content:
              'Discover how smart reminders adapt to customer preferences, send via their preferred channel, and include personalized details.',
          },
          benefits: [
            '85% no-show reduction',
            'Personalized messages',
            'Preferred channels',
            'Automated follow-ups',
          ],
        },
        {
          icon: 'Users',
          title: 'Staff Coordination Hub',
          description:
            'Coordinate multiple staff members, manage availability, and distribute bookings intelligently.',
          demo: {
            title: 'Staff Management Demo',
            content:
              'Learn how to manage multiple staff schedules, set availability rules, and automatically distribute appointments.',
          },
          benefits: [
            'Multi-staff support',
            'Availability rules',
            'Smart distribution',
            'Performance tracking',
          ],
        },
        {
          icon: 'BarChart3',
          title: 'Analytics & Insights',
          description:
            'Comprehensive reporting on booking patterns, revenue trends, and customer behavior analytics.',
          demo: {
            title: 'Analytics Demo',
            content:
              'Explore detailed analytics showing booking trends, revenue insights, customer behavior, and performance metrics.',
          },
          benefits: [
            'Revenue tracking',
            'Booking patterns',
            'Customer insights',
            'Performance metrics',
          ],
        },
      ],
      demoSection: {
        title: 'See It In Action',
        subtitle: 'Experience how Slotyfi transforms your booking process',
        ctaText: 'Try Interactive Demo',
        watchText: 'Watch Video Demo',
      },
    },
    fa: {
      title: 'ویژگی‌های قدرتمند که برای شما کار می‌کنند',
      subtitle:
        'ابزارهایی را کشف کنید که فرآیند رزرو شما را از آشفتگی به خودکارسازی یکپارچه تبدیل می‌کنند',
      features: [
        {
          icon: 'Calendar',
          title: 'مدیریت هوشمند تقویم',
          description:
            'برنامه‌ریزی مبتنی بر هوش مصنوعی که از تضاد جلوگیری می‌کند و زمان‌بندی شما را خودکار بهینه می‌کند.',
          demo: {
            title: 'دمو تقویم',
            content:
              'ببینید تقویم هوشمند ما چگونه تضادها را خودکار تشخیص می‌دهد، زمان‌های بهینه پیشنهاد می‌کند و در زمان واقعی در همه دستگاه‌ها همگام‌سازی می‌کند.',
          },
          benefits: [
            'جلوگیری از تضاد',
            'بهینه‌سازی خودکار',
            'همگام‌سازی زمان واقعی',
            'پشتیبانی چند کارمند',
          ],
        },
        {
          icon: 'Bot',
          title: 'دستیار خودکار رزرو هوش مصنوعی',
          description:
            'چت‌بات هوشمند سناریوهای پیچیده رزرو را مدیریت می‌کند، زمینه را درک می‌کند و تعاملات انسان‌مانند ارائه می‌دهد.',
          demo: {
            title: 'دمو دستیار هوش مصنوعی',
            content:
              'تجربه کنید هوش مصنوعی ما چگونه زبان طبیعی را درک می‌کند، درخواست‌های پیچیده را مدیریت می‌کند و پاسخ‌های شخصی‌سازی شده 24/7 ارائه می‌دهد.',
          },
          benefits: [
            'دسترسی 24/7',
            'زبان طبیعی',
            'آگاهی زمینه‌ای',
            'پشتیبانی چندزبانه',
          ],
        },
        {
          icon: 'MessageSquare',
          title: 'یکپارچگی چندکاناله',
          description:
            'تلگرام، واتساپ، اینستاگرام و وب‌سایت خود را از طریق یک پلتفرم یکپارچه به‌طور یکپارچه متصل کنید.',
          demo: {
            title: 'دمو یکپارچگی',
            content:
              'ببینید رزروهای کانال‌های مختلف چگونه خودکار با داشبورد مرکزی شما با پروفایل‌های مشتری یکپارچه همگام‌سازی می‌شوند.',
          },
          benefits: [
            'داشبورد یکپارچه',
            'همگام‌سازی متقابل پلتفرم',
            'پروفایل‌های مشتری',
            'تجزیه و تحلیل کانال',
          ],
        },
        {
          icon: 'Bell',
          title: 'سیستم یادآوری هوشمند',
          description:
            'یادآوری‌های خودکار از طریق کانال‌های ترجیحی غیبت را 85% کاهش می‌دهد با پیام‌رسانی شخصی‌سازی شده.',
          demo: {
            title: 'دمو یادآوری‌ها',
            content:
              'کشف کنید یادآوری‌های هوشمند چگونه با ترجیحات مشتری تطبیق می‌یابند، از طریق کانال ترجیحی آن‌ها ارسال می‌شوند و جزئیات شخصی‌سازی شده شامل می‌شوند.',
          },
          benefits: [
            '85% کاهش غیبت',
            'پیام‌های شخصی‌سازی شده',
            'کانال‌های ترجیحی',
            'پیگیری‌های خودکار',
          ],
        },
        {
          icon: 'Users',
          title: 'مرکز هماهنگی کارکنان',
          description:
            'چندین عضو کارکنان را هماهنگ کنید، دسترسی را مدیریت کنید و رزروها را به‌طور هوشمند توزیع کنید.',
          demo: {
            title: 'دمو مدیریت کارکنان',
            content:
              'یاد بگیرید چگونه برنامه‌های چندین کارمند را مدیریت کنید، قوانین دسترسی تنظیم کنید و قرارملاقات‌ها را خودکار توزیع کنید.',
          },
          benefits: [
            'پشتیبانی چند کارمند',
            'قوانین دسترسی',
            'توزیع هوشمند',
            'ردیابی عملکرد',
          ],
        },
        {
          icon: 'BarChart3',
          title: 'تجزیه و تحلیل و بینش‌ها',
          description:
            'گزارش‌دهی جامع در مورد الگوهای رزرو، روندهای درآمد و تجزیه و تحلیل رفتار مشتری.',
          demo: {
            title: 'دمو تجزیه و تحلیل',
            content:
              'تجزیه و تحلیل‌های تفصیلی نشان‌دهنده روندهای رزرو، بینش‌های درآمد، رفتار مشتری و معیارهای عملکرد را کشف کنید.',
          },
          benefits: [
            'ردیابی درآمد',
            'الگوهای رزرو',
            'بینش‌های مشتری',
            'معیارهای عملکرد',
          ],
        },
      ],
      demoSection: {
        title: 'آن را در عمل ببینید',
        subtitle: 'تجربه کنید اسلاتیفای چگونه فرآیند رزرو شما را تغییر می‌دهد',
        ctaText: 'دمو تعاملی را امتحان کنید',
        watchText: 'دمو ویدیویی را تماشا کنید',
      },
    },
  };

  const currentContent = content?.[language];

  return (
    <section ref={sectionRef} id="features" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {currentContent?.features?.map((feature, index) => (
            <div
              key={index}
              ref={(el) => (featuresRef.current[index] = el)}
              className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${
                activeFeature === index ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setActiveFeature(index)}>
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={feature?.icon} size={24} color="#2563EB" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature?.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature?.description}
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="space-y-2">
                {feature?.benefits?.map((benefit, benefitIndex) => (
                  <div key={benefitIndex} className="flex items-center text-sm">
                    <Icon
                      name="Check"
                      size={16}
                      color="#059669"
                      className="mr-2 flex-shrink-0"
                    />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Hover Demo Button */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-blue-600 hover:bg-blue-50"
                  iconName="Play"
                  iconPosition="left">
                  View Demo
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Interactive Demo Section */}
        <div ref={demoRef} className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-2">
                {currentContent?.demoSection?.title}
              </h3>
              <p className="text-blue-100">
                {currentContent?.demoSection?.subtitle}
              </p>
            </div>

            <div className="p-8">
              {/* Active Feature Demo */}
              <div
                className={`${language === 'fa' ? 'text-right' : 'text-left'}`}>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">
                  {currentContent?.features?.[activeFeature]?.demo?.title}
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {currentContent?.features?.[activeFeature]?.demo?.content}
                </p>

                {/* Demo Mockup */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <Icon
                          name={currentContent?.features?.[activeFeature]?.icon}
                          size={16}
                          color="white"
                        />
                      </div>
                      <span className="font-medium">
                        {currentContent?.features?.[activeFeature]?.title}
                      </span>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-gray-500">Live Demo</span>
                    </div>
                  </div>

                  <div className="bg-white rounded border-2 border-dashed border-gray-300 p-8 text-center">
                    <Icon
                      name="Play"
                      size={48}
                      color="#6B7280"
                      className="mx-auto mb-4"
                    />
                    <p className="text-gray-500">
                      Interactive demo would load here
                    </p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div
                  className={`flex flex-col sm:flex-row gap-4 ${
                    language === 'fa' ? 'sm:justify-end' : 'sm:justify-start'
                  }`}>
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                    iconName="MousePointer"
                    iconPosition="left">
                    {currentContent?.demoSection?.ctaText}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Video"
                    iconPosition="left">
                    {currentContent?.demoSection?.watchText}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
